import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  handleStatusFilterOptions,
  QC_FILTER_OPTIONS,
} from "../../../utils/constants/index";
import {
  SOURCE_FILTER_OPTIONS,
  dateParam,
  day,
  optionLabel,
  peopleLabel,
} from "../../../utils/constants/listFilters";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useFilterDraft from "../../../hooks/useFilterDraft";
import DateRangeFilter from "../../common/DateRangeFilter";
import FilterSelect from "../../common/FilterSelect";
import ActiveFilterChips from "../../common/ActiveFilterChips";
import RequestedByFilter from "../../common/RequestedByFilter";
import FiltersDrawer, {
  FilterField,
  FilterSection,
} from "../../common/FiltersDrawer";
import { Input } from "../../ui/input";

const EMPTY_PANEL = {
  qcStatus: "",
  source: "",
  requestedBy: "",
  requestedByLabel: "",
};

/** Ad Delivery filters: bar, drawer and chips, mirrored into the URL; reports API params via onChange. */
const DeliveryFilterBar = ({
  rejectedOnly,
  status,
  onStatusChange,
  agencyId,
  onChange,
  actions,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("query") || ""
  );
  const [dates, setDates] = useState(() => ({
    from: dateParam(searchParams, "date_from"),
    to: dateParam(searchParams, "date_to"),
  }));
  const [panel, setPanel] = useState(() => ({
    ...EMPTY_PANEL,
    qcStatus: searchParams.get("qc_status") || "",
    source: searchParams.get("source") || "",
    requestedBy: searchParams.get("requested_by") || "",
  }));
  const searchQuery = useDebouncedValue(searchInput, 400);
  const { open, draft, setDraft, onOpenChange, apply } = useFilterDraft(
    panel,
    setPanel
  );

  const apiParams = {
    search: searchQuery.trim() || undefined,
    date_from: day(dates.from) || undefined,
    date_to: day(dates.to) || undefined,
    qc_status: panel.qcStatus || undefined,
    requested_by_id: panel.requestedBy ? Number(panel.requestedBy) : undefined,
    requested_via: panel.source || undefined,
  };
  const filterKey = JSON.stringify(apiParams);

  useEffect(() => {
    onChange({ apiParams: JSON.parse(filterKey), key: filterKey });
  }, [filterKey, onChange]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val) =>
          val ? next.set(key, val) : next.delete(key);
        setOrDelete("query", searchQuery.trim());
        setOrDelete("date_from", day(dates.from));
        setOrDelete("date_to", day(dates.to));
        setOrDelete("qc_status", panel.qcStatus);
        setOrDelete("requested_by", panel.requestedBy);
        setOrDelete("source", panel.source);
        return next;
      },
      { replace: true }
    );
  }, [searchQuery, dates, panel, setSearchParams]);

  const chips = [
    panel.qcStatus && {
      key: "qc",
      label: `QC: ${optionLabel(QC_FILTER_OPTIONS, panel.qcStatus)}`,
      onRemove: () => setPanel((p) => ({ ...p, qcStatus: "" })),
    },
    panel.source && {
      key: "source",
      label: `Source: ${optionLabel(SOURCE_FILTER_OPTIONS, panel.source)}`,
      onRemove: () => setPanel((p) => ({ ...p, source: "" })),
    },
    panel.requestedBy && {
      key: "requested_by",
      label: `Requested by: ${panel.requestedByLabel || peopleLabel([], panel.requestedBy)}`,
      onRemove: () =>
        setPanel((p) => ({ ...p, requestedBy: "", requestedByLabel: "" })),
    },
  ].filter(Boolean);

  const hasAnyFilter = !!(
    chips.length ||
    searchInput ||
    dates.from ||
    dates.to ||
    status
  );

  const clearAll = () => {
    setPanel(EMPTY_PANEL);
    setSearchInput("");
    setDates({ from: null, to: null });
    onStatusChange("");
  };

  const setField = (key) => (value) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="min-w-[220px] flex-1 h-9 bg-white border-gray-300"
        />
        <DateRangeFilter
          label={rejectedOnly ? "Rejected Date" : "Delivered Date"}
          from={dates.from}
          to={dates.to}
          onChange={(from, to) => setDates({ from, to })}
        />
        <FilterSelect
          value={status}
          onChange={onStatusChange}
          allLabel="All statuses"
          placeholder="All statuses"
          options={handleStatusFilterOptions({ isOnDeliveryPage: true })}
        />
        <FiltersDrawer
          count={chips.length}
          open={open}
          onOpenChange={onOpenChange}
          onApply={apply}
          onReset={() => setDraft(EMPTY_PANEL)}
        >
          <FilterSection title="Status">
            <FilterField label="QC status">
              <FilterSelect
                value={draft.qcStatus}
                onChange={setField("qcStatus")}
                allLabel="All QC statuses"
                placeholder="QC status"
                options={QC_FILTER_OPTIONS}
                width="w-full"
                className="w-full"
              />
            </FilterField>
            <FilterField label="Source">
              <FilterSelect
                value={draft.source}
                onChange={setField("source")}
                allLabel="All sources"
                placeholder="Source"
                options={SOURCE_FILTER_OPTIONS}
                width="w-full"
                className="w-full"
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="People">
            <FilterField label="Requested by" wide>
              <RequestedByFilter
                value={draft.requestedBy}
                onChange={(id, label) =>
                  setDraft((d) => ({
                    ...d,
                    requestedBy: id,
                    requestedByLabel: label,
                  }))
                }
                agencyId={agencyId || undefined}
                placeholder="Anyone"
                fullWidth
              />
            </FilterField>
          </FilterSection>
        </FiltersDrawer>
        {actions}
      </div>
      {hasAnyFilter && (
        <ActiveFilterChips chips={chips} onClearAll={clearAll} />
      )}
    </>
  );
};

export default DeliveryFilterBar;
